import { api } from './api';

export interface HeroContent {
  title: string;
  subtitle: string;
  backgroundImage?: string;
}

export interface AboutContent {
  title: string;
  content: string;
  image?: string;
}

export interface ServiceItem {
  title: string;
  description: string;
  icon?: string;
  image?: string;
}

export interface ServicesContent {
  title: string;
  items: ServiceItem[];
}

export interface ContactContent {
  title: string;
  phone: string;
  phone2?: string;
  email: string;
  address: string;
  workshop?: string;
}

export interface FooterSection {
  title: string;
  links: string[];
}

export interface FooterContent {
  companyName: string;
  description: string;
  logo?: string;
  socialLinks: {
    facebook: string;
    instagram: string;
  };
  sections: FooterSection[];
}

export interface SEOContent {
  title: string;
  description: string;
  keywords: string;
  ogImage: string;
}

export interface SiteContent {
  hero: HeroContent;
  about: AboutContent;
  services: ServicesContent;
  contact: ContactContent;
  footer: FooterContent;
  seo: SEOContent;
}

class ContentService {
  private cache: SiteContent | null = null;
  private cacheExpiry: number = 0;
  private cacheDuration = 5 * 60 * 1000; // 5 minutes

  async getContent(): Promise<SiteContent> {
    const now = Date.now();
    
    // Return cached data if still valid
    if (this.cache && now < this.cacheExpiry) {
      return this.cache;
    }

    try {
      const response = await api.getContent();
      const content = response.data;
      
      this.cache = content;
      this.cacheExpiry = now + this.cacheDuration;
      return content;
    } catch (error) {
      console.error('Error fetching content:', error);
      
      // Return fallback data if API fails
      return this.getFallbackData();
    }
  }

  async updateContent(content: SiteContent): Promise<SiteContent> {
    try {
      const response = await api.updateContent(content);
      this.invalidateCache();
      return response.data;
    } catch (error) {
      console.error('Error updating content:', error);
      throw error;
    }
  }

  invalidateCache(): void {
    this.cache = null;
    this.cacheExpiry = 0;
  }

  private getFallbackData(): SiteContent {
    return {
      hero: {
        title: "Twoja Przestrzeń, Nasze Doświadczenie",
        subtitle: "Tworzymy ogrody zimowe i zabudowy tarasów, które przekształcą Twój dom w wyjątkowe miejsce"
      },
      about: {
        title: "O Nas",
        content: "Jesteśmy specjalistami w projektowaniu i wykonawstwie ogrodów zimowych oraz zabudów tarasów. Nasze wieloletnie doświadczenie pozwala nam realizować projekty o najwyższej jakości."
      },
      services: {
        title: "Nasze Usługi",
        items: [
          {
            title: "Ogrody zimowe",
            description: "Projektujemy i budujemy nowoczesne ogrody zimowe, które pozwolą Ci cieszyć się przestrzenią przez cały rok.",
            icon: "🌿"
          },
          {
            title: "Zabudowa tarasów",
            description: "Kompleksowe rozwiązania zabudowy tarasów z wykorzystaniem najnowszych technologii.",
            icon: "🏠"
          },
          {
            title: "Przeszklenia",
            description: "Nowoczesne systemy przeszkleniowe, które zwiększą funkcjonalność Twojej przestrzeni.",
            icon: "🪟"
          }
        ]
      },
      contact: {
        title: "Kontakt",
        phone: "+48 600 375 251",
        phone2: "+48 692 417 607",
        email: "aluxim@aluxim.pl",
        address: "Siedziba: Ul. Gorzowska 54, 64-980 Trzcianka",
        workshop: "Warsztat: Ul. Popiełuszki 11, 64-980 Trzcianka"
      },
      footer: {
        companyName: "Aluxim",
        description: "Profesjonalne rozwiązania budowlane dla Twojego domu i biznesu.",
        socialLinks: {
          facebook: "#",
          instagram: "#"
        },
        sections: [
          {
            title: "Nasze usługi",
            links: ["Ogrody zimowe", "Zabudowa tarasów", "Zabudowa balkonów", "System bezramowy", "Zadaszenia tarasów", "Daszki"]
          },
          {
            title: "Przydatne linki",
            links: ["Usługi", "O nas", "Realizacje", "Transformacje", "Galeria", "Kontakt"]
          }
        ]
      },
      seo: {
        title: "Aluxim | Ogrody zimowe, zabudowy tarasów i balkonów",
        description: "Profesjonalne ogrody zimowe, zabudowy tarasów i balkonów. Nowoczesne rozwiązania dla Twojego domu od firmy Aluxim z wieloletnim doświadczeniem.",
        keywords: "ogrody zimowe, zabudowy tarasów, zabudowy balkonów, system bezramowy, zadaszenia tarasów, daszki",
        ogImage: "https://www.aluxim.pl/og-image.jpg"
      }
    };
  }
}

export const contentService = new ContentService();
export default contentService;
